import React, {useEffect, useState, useCallback, useRef} from 'react';
import {View, Text, TouchableOpacity} from 'react-native';
import Entypo from 'react-native-vector-icons/Entypo';
import {Colors} from '../../../../../constants/Colors';
import ButtonComponent from '../../../../../components/ButtonComponent/ButtonComponent';
import {styles} from './styles';

import { useNavigation } from '@react-navigation/native';
import { NavigationProps } from '../../../../../navigation/Routes';
import { AppRoutes } from '../../../../../navigation/Routes.types';


interface ICounter {
  width?: string;
  height?: number;
  mt?: number;
  directionButton?: string;
  buttonWidth?: string;
  hasButton?: boolean;
  hasRanges?: boolean;
  hasMoreFive?: boolean;
  quantity?: number;
  counterPosition?: string;
  max?: number;
  onPress?: (val: number) => void;
  increment?: (count: number) => void;
  decrement?: (count: number) => void;
  item?: Object;
  bag?: boolean;
}

const Counter = React.memo(
  ({
    width = '100%',
    height = 30,
    mt = 20,
    directionButton,
    buttonWidth = '100%',
    hasButton = true,
    counterPosition = 'center',
    onPress,
    quantity,
    increment,
    decrement,
    hasRanges = false,
    hasMoreFive = false,
    max,
    item,
    bag = false,
  }: ICounter) => {
    const [count, setCount] = useState<number>(quantity || 0);
    const [isAdded, setIsAdded] = useState<boolean>(false);
    const [initialCount, setInitialCount] = useState<number>(quantity || 0);
    const [isInCart, setIsInCart] = useState<boolean>((quantity || 0) > 0);

    // Refs to always have the latest callbacks without stale closures
    const onPressRef = useRef(onPress);
    const incrementRef = useRef(increment);
    const decrementRef = useRef(decrement);
    useEffect(() => { onPressRef.current = onPress; }, [onPress]);
    useEffect(() => { incrementRef.current = increment; }, [increment]);
    useEffect(() => { decrementRef.current = decrement; }, [decrement]);

    const { navigate } = useNavigation<NavigationProps>();

    useEffect(() => {
      setCount(quantity || 0);
      setInitialCount(quantity || 0);
      setIsInCart((quantity || 0) > 0);
    }, [quantity]);

    const handleAdd = useCallback(() => {
      setCount(prev => {
        onPressRef.current && onPressRef.current(prev);
        return prev;
      });
      setIsAdded(true);
    }, []);

    const handleUpdate = useCallback(() => {
      setCount(prev => {
        onPressRef.current && onPressRef.current(prev);
        return prev;
      });
      setIsAdded(true);
    }, []);

    const handleIncrement = useCallback(
      (value: number) => {
        setCount(prev => {
          if (max !== undefined && prev + value > max) return prev;
          const next = prev + value;
          incrementRef.current && incrementRef.current(next);
          setIsAdded(false);
          if (!hasButton) {
            onPressRef.current && onPressRef.current(next);
          }
          return next;
        });
      },
      [max, hasButton],
    );

    const handleDecrement = useCallback(() => {
      setCount(prev => {
        if (prev === 0) return prev;
        const next = prev - 1;
        decrementRef.current && decrementRef.current(next);
        setIsAdded(false);
        if (!hasButton) {
          onPressRef.current && onPressRef.current(next);
        }
        return next;
      });
    }, [hasButton]);
    

    return (
      <View
        style={{
          ...styles.space,
          alignItems: counterPosition,
          flexDirection: directionButton,
        }}
      >
        <View
          style={{
            ...styles.container,
            width: width,
            height: height,
            marginTop: mt,
          }}>
          <TouchableOpacity onPress={handleDecrement}>
            <Entypo name="minus" size={20} color={Colors.RedPallete.red} />
          </TouchableOpacity>
          <Text
            style={styles.count}
            onPress={() => {
              if (bag) return;
              navigate(AppRoutes.PRODUCT_DETAILS, {item: item});
            }}
          >
            {count}
          </Text>
          <TouchableOpacity
            onPress={count === max ? () => {} : () => handleIncrement(1)}
            disabled={count === max}>
            <Entypo
              name="plus"
              size={20}
              color={count === max ? 'gray' : Colors.RedPallete.red}
            />
          </TouchableOpacity>
          {hasMoreFive && (
          <View
            style={{position: 'absolute', right: -45, top: -7, flexDirection: 'row'
            }}>
            <TouchableOpacity
              disabled={max ? count + 5 > max : false}
              onPress={() => handleIncrement(5)}
              style={[styles.smallButton,{
                backgroundColor:
                  max && count + 5 > max ? 'gray' : Colors.RedPallete.red,
              },]}>
              <Text style={styles.buttonText}>+5</Text>
            </TouchableOpacity>
            </View>
        )}
        </View>

        {hasRanges && (
          <View
            style={{
              flexDirection: 'row',
              justifyContent: 'space-between',
            }}>
            <TouchableOpacity
              disabled={max ? count + 5 > max : false}
              onPress={() => handleIncrement(5)}
              style={[
                styles.smallButton,
                {
                  backgroundColor:
                    max && count + 5 > max ? 'gray' : Colors.RedPallete.red,
                },
              ]}>
              <Text style={styles.buttonText}>+5</Text>
            </TouchableOpacity>
            <TouchableOpacity
              disabled={max ? count + 10 > max : false}
              onPress={() => handleIncrement(10)}
              style={[
                styles.smallButton,
                {
                  backgroundColor:
                    max && count + 10 > max ? 'gray' : Colors.RedPallete.red,
                },
              ]}>
              <Text style={styles.buttonText}>+10</Text>
            </TouchableOpacity>
            <TouchableOpacity
              disabled={max ? count + 20 > max : false}
              onPress={() => handleIncrement(20)}
              style={[
                styles.smallButton,
                {
                  backgroundColor:
                    max && count + 20 > max ? 'gray' : Colors.RedPallete.red,
                },
              ]}>
              <Text style={styles.buttonText}>+20</Text>
            </TouchableOpacity>
          </View>
        )}
        {hasButton && (
          <>
            {isAdded && count === initialCount && count !== 0 ? (
              <ButtonComponent
                title="Adicionado"
                width={buttonWidth}
                onPress={() => {}}
                backgroundColor="#21ce2f"
              />
            ) : isInCart ? (
              <ButtonComponent
                title="Atualizar"
                width={buttonWidth}
                onPress={handleUpdate}
                backgroundColor="#1f77ec"
              />
            ) : (
              <ButtonComponent
                title="Adicionar"
                width={buttonWidth}
                onPress={handleAdd}
              />
            )}
          </>
        )}
      </View>
    );
  },
);

export default Counter;
